import React, { useState, useEffect } from 'react';
import { fileAPI, adminAPI } from '../lib/api';
import SearchableDropdown from './SearchableDropdown';

const FileShareModal = ({ file, isOpen, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [shareType, setShareType] = useState('user'); // 'user', 'role', 'investor'
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [permissionType, setPermissionType] = useState('VIEW');
    const [expiresAt, setExpiresAt] = useState('');
    const [notes, setNotes] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [investors, setInvestors] = useState([]);
    const [currentPermissions, setCurrentPermissions] = useState([]);

    useEffect(() => {
        if (isOpen && file) {
            loadData();
        }
    }, [isOpen, file]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            // For startups, load only related investors and events
            // For other users, load all users
            try {
                const relatedInvestorsData = await fileAPI.getRelatedInvestors();
                setInvestors(relatedInvestorsData || []);
                // Also set as allUsers for the "Specific Users" tab to show only related investors
                setAllUsers(relatedInvestorsData || []);
            } catch (err) {
                console.log('Could not load related investors, falling back to all users');
                // Fallback to all users if the endpoint fails (e.g., user is not a startup)
                try {
                    const usersData = await adminAPI.getAllUsers();
                    setAllUsers(usersData || []);
                    const investorsData = await adminAPI.getInvestors();
                    setInvestors(investorsData || []);
                } catch (fallbackErr) {
                    console.error('Failed to load users:', fallbackErr);
                }
            }

            // Load current permissions
            if (file.permissions) {
                setCurrentPermissions(file.permissions);
            }
        } catch (err) {
            setError('Failed to load sharing data');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleShare = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const baseShareData = {
                fileId: file.id,
                expiresAt: expiresAt || null,
                notes: notes || null
            };

            if (shareType === 'user') {
                if (selectedUsers.length === 0) {
                    setError('Please select at least one user');
                    setLoading(false);
                    return;
                }
                // Share with multiple users - grant both VIEW and DOWNLOAD
                for (const userId of selectedUsers) {
                    // Grant VIEW permission
                    await fileAPI.shareFile({
                        ...baseShareData,
                        userId,
                        permissionType: 'VIEW'
                    });
                    // Grant DOWNLOAD permission
                    await fileAPI.shareFile({
                        ...baseShareData,
                        userId,
                        permissionType: 'DOWNLOAD'
                    });
                }
                setSuccess(`File shared with ${selectedUsers.length} user(s)`);
            } else if (shareType === 'role') {
                if (!selectedRole) {
                    setError('Please select a role');
                    setLoading(false);
                    return;
                }
                // Grant both VIEW and DOWNLOAD to role
                await fileAPI.shareFile({
                    ...baseShareData,
                    role: selectedRole,
                    permissionType: 'VIEW'
                });
                await fileAPI.shareFile({
                    ...baseShareData,
                    role: selectedRole,
                    permissionType: 'DOWNLOAD'
                });
                setSuccess(`File shared with ${selectedRole} role`);
            } else if (shareType === 'investor') {
                if (selectedUsers.length === 0) {
                    setError('Please select at least one investor');
                    setLoading(false);
                    return;
                }
                // Share with specific investors - grant both VIEW and DOWNLOAD
                for (const investorId of selectedUsers) {
                    // Grant VIEW permission
                    await fileAPI.shareFile({
                        ...baseShareData,
                        userId: investorId,
                        permissionType: 'VIEW'
                    });
                    // Grant DOWNLOAD permission
                    await fileAPI.shareFile({
                        ...baseShareData,
                        userId: investorId,
                        permissionType: 'DOWNLOAD'
                    });
                }
                setSuccess(`File shared with ${selectedUsers.length} investor(s)`);
            }

            // Reload permissions
            await loadData();

            // Reset form
            setSelectedUsers([]);
            setSelectedRole('');
            setExpiresAt('');
            setNotes('');

            // Call success callback after a delay
            setTimeout(() => {
                onSuccess && onSuccess();
            }, 1500);

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to share file');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRevokePermission = async (permissionId) => {
        if (!window.confirm('Are you sure you want to revoke this permission?')) {
            return;
        }

        try {
            await fileAPI.revokePermission(permissionId);
            setSuccess('Permission revoked successfully');
            await loadData();
        } catch (err) {
            setError('Failed to revoke permission');
            console.error(err);
        }
    };

    if (!isOpen) return null;

    const roles = [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'INVESTOR', label: 'Investor' },
        { value: 'STARTUP', label: 'Startup' },
        { value: 'INCUBATOR', label: 'Incubator' }
    ];

    const permissionTypes = [
        { value: 'VIEW', label: 'View', description: 'Can view file metadata', icon: '👁️' },
        { value: 'DOWNLOAD', label: 'Download', description: 'Can download the file', icon: '⬇️' }
    ];

    const userOptions = allUsers
        .filter(u => shareType === 'investor' ? u.role === 'INVESTOR' : true)
        .map(u => ({
            value: u.id,
            label: `${u.name} (${u.email})`,
            description: u.organizationName || u.role
        }));

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900">Share File</h3>
                        <p className="text-sm text-gray-500 mt-1">{file?.originalName}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Error/Success Messages */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                            {success}
                        </div>
                    )}

                    {/* Share Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                            Share With
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                            <button
                                onClick={() => setShareType('user')}
                                className={`p-4 border-2 rounded-lg transition-all ${shareType === 'user'
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                    }`}
                            >
                                <div className="text-2xl mb-2">👤</div>
                                <div className="text-sm font-medium text-gray-900">Specific Users</div>
                                <div className="text-xs text-gray-500 mt-1">Share with individual users</div>
                            </button>
                            <button
                                onClick={() => setShareType('role')}
                                className={`p-4 border-2 rounded-lg transition-all ${shareType === 'role'
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                    }`}
                            >
                                <div className="text-2xl mb-2">👥</div>
                                <div className="text-sm font-medium text-gray-900">Role</div>
                                <div className="text-xs text-gray-500 mt-1">Share with all users in a role</div>
                            </button>
                            <button
                                onClick={() => setShareType('investor')}
                                className={`p-4 border-2 rounded-lg transition-all ${shareType === 'investor'
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-blue-300'
                                    }`}
                            >
                                <div className="text-2xl mb-2">💼</div>
                                <div className="text-sm font-medium text-gray-900">Investors</div>
                                <div className="text-xs text-gray-500 mt-1">Share with specific investors</div>
                            </button>
                        </div>
                    </div>

                    {/* User/Investor Selection */}
                    {(shareType === 'user' || shareType === 'investor') && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select {shareType === 'investor' ? 'Investors' : 'Users'}
                            </label>
                            <SearchableDropdown
                                options={userOptions}
                                value={selectedUsers}
                                onChange={setSelectedUsers}
                                placeholder={`Search and select ${shareType === 'investor' ? 'investors' : 'users'}...`}
                                multiple={true}
                            />
                        </div>
                    )}

                    {/* Role Selection */}
                    {shareType === 'role' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Role
                            </label>
                            <select
                                value={selectedRole}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Select a role...</option>
                                {roles.map(role => (
                                    <option key={role.value} value={role.value}>
                                        {role.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Info about permissions */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <p className="text-sm font-medium text-blue-900">Shared users will receive:</p>
                                <ul className="text-sm text-blue-800 mt-1 space-y-1">
                                    <li>• <strong>View</strong> permission - Can view file metadata</li>
                                    <li>• <strong>Download</strong> permission - Can download the file</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Expiration Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expiration Date (Optional)
                        </label>
                        <input
                            type="datetime-local"
                            value={expiresAt}
                            onChange={(e) => setExpiresAt(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Leave empty for permanent access
                        </p>
                    </div>

                    {/* Notes */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Notes (Optional)
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Add any notes about this permission..."
                        />
                    </div>

                    {/* Current Permissions */}
                    {currentPermissions.length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3">
                                Current Permissions ({currentPermissions.length})
                            </h4>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {currentPermissions.map(perm => (
                                    <div
                                        key={perm.id}
                                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {perm.userName || perm.role || 'Network'}
                                                </span>
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                    {perm.permissionType}
                                                </span>
                                                {perm.isActive === false && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                        Revoked
                                                    </span>
                                                )}
                                            </div>
                                            {perm.userEmail && (
                                                <div className="text-xs text-gray-500 mt-1">{perm.userEmail}</div>
                                            )}
                                            {perm.expiresAt && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Expires: {new Date(perm.expiresAt).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                        {perm.isActive !== false && (
                                            <button
                                                onClick={() => handleRevokePermission(perm.id)}
                                                className="ml-2 p-1 text-red-600 hover:text-red-800 transition"
                                                title="Revoke permission"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                            Close
                        </button>
                        <button
                            onClick={handleShare}
                            disabled={loading}
                            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    <span>Sharing...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                    <span>Share File</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FileShareModal;
